import { useTanstackSpotsFetchFn } from '@/components/Functional/hooks-core/useTanstackFetchFn'
import { haversineDistance } from '@/components/Functional/utils/calcGeoLocationDistance'
import { buildSheetById, isSpotFullyLocked } from '@/components/Functional/utils/calcSheetLockStatus'
import type { SpotData, SpotListSheetData } from '@/schemas/v2/spot'
import { useTokenStore } from '@/store/useTokenStore'
import { useQueryClient } from '@tanstack/react-query'
import { useMachine } from '@xstate/react'
import { useEffect } from 'react'
import { assign, fromPromise, setup } from 'xstate'

/** ダイアログに表示するスポット候補 */
export type NearbySpotItem = {
  spotId: number
  spotName: string
  address: string | null
  /** GPS 距離（km）。null = GPS 取得不可フォールバック */
  distanceKm: number | null
  /** スタンプ獲得済みか（認証済み時のみ設定） */
  isAcquired: boolean
  /** スポット代表画像 URL（images[0]）。未設定なら null */
  imageUrl: string | null
  /** 所属シートが全てロック中か（スポット一覧と同様に行を灰色表示にする） */
  grayedOut: boolean
}

type ApplicationTriggerContext = {
  isAuthenticated: boolean
  isAuthInitialized: boolean
  spots: SpotData[]
  /** 一覧レスポンスのシート情報（locked の判定に使用） */
  sheets: SpotListSheetData[]
  gps: {
    lat: number
    lon: number
    available: boolean
  }
  nearbySpots: NearbySpotItem[]
  selectedSpotId: number | null
  errorMessage: string | null
}

type ApplicationTriggerEvent =
  | { type: 'AUTH_READY'; isAuthenticated: boolean }
  | { type: 'TRIGGER' }
  | { type: 'SPOT_SELECTED'; spotId: number }
  | { type: 'DIALOG_CLOSE' }
  | { type: 'RESET' }

export type GPSResult = { lat: number; lon: number; available: boolean }

/** GET /api/v2/spots の取得結果（シート情報はロック判定に使うため spots と併せて保持する） */
type SpotsResult = { spots: SpotData[]; sheets: SpotListSheetData[] }

/** 測位の打ち切り時間。これを過ぎたら GPS なし（ID 順フォールバック）で確定する */
export const GPS_TIMEOUT = 10000

/** 許容する測位キャッシュの古さ。並び替え用途なので直近の測位を再利用してよい */
const GPS_MAXIMUM_AGE = 60000

const gpsUnavailable = (): GPSResult => ({ lat: 0, lon: 0, available: false })

/**
 * 近くのスポットを距離順に並べるための測位。
 *
 * スタンプ獲得の距離判定に使う測位（#3445 の精度ゲート付き `watchPosition`）とは要件が異なる。
 * こちらは最大 3 件の並び替えと距離表示にしか使わず、獲得可否の判定には一切使わないため、
 * **精度より「測位が取れること」を優先**する。
 *
 * - `enableHighAccuracy: false` — 高精度測位は GPS を持たない環境（PC 等）で TIMEOUT（code 3）が
 *   頻発し、常に「位置情報が取得できなかったためID順に表示しています」になっていた（#3472）。
 * - `maximumAge` を許容 — 直近の測位があれば即座に確定でき、待ち時間が実質ゼロになる。
 * - `watchPosition` を使用 — W3C 仕様上、TIMEOUT / POSITION_UNAVAILABLE では watch が継続するため
 *   一時的な失敗から復帰できる。`getCurrentPosition` は 1 回のエラーで確定してしまう。
 *   精度ゲートは設けず、最初に届いた測位をそのまま採用する。
 */
export const getGPS = (): Promise<GPSResult> =>
  new Promise<GPSResult>((resolve) => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      resolve(gpsUnavailable())
      return
    }

    let settled = false
    let watchId: number | null = null
    let timerId: ReturnType<typeof setTimeout> | null = null

    const settle = (result: GPSResult) => {
      if (settled) return
      settled = true
      if (timerId !== null) clearTimeout(timerId)
      if (watchId !== null) navigator.geolocation.clearWatch(watchId)
      resolve(result)
    }

    timerId = setTimeout(() => settle(gpsUnavailable()), GPS_TIMEOUT)

    const id = navigator.geolocation.watchPosition(
      ({ coords }) => settle({ lat: coords.latitude, lon: coords.longitude, available: true }),
      (error) => {
        // 権限拒否は待っても状況が変わらないため即座に打ち切る。
        // TIMEOUT / POSITION_UNAVAILABLE は watch が継続するため、打ち切り時間まで測位を待つ。
        if (error.code === error.PERMISSION_DENIED) settle(gpsUnavailable())
      },
      { enableHighAccuracy: false, timeout: GPS_TIMEOUT, maximumAge: GPS_MAXIMUM_AGE },
    )

    // コールバックが同期的に発火して settle 済みになっている場合に watch が残らないようにする
    if (settled) {
      navigator.geolocation.clearWatch(id)
    } else {
      watchId = id
    }
  })

export const applicationTriggerMachineBase = setup({
  types: {
    context: {} as ApplicationTriggerContext,
    events: {} as ApplicationTriggerEvent,
    input: {} as { isAuthenticated: boolean; isAuthInitialized: boolean },
  },
  actors: {
    fetchGPS: fromPromise<GPSResult, void>(async () => ({ lat: 0, lon: 0, available: false })),
    fetchSpots: fromPromise<SpotsResult, void>(async () => ({ spots: [], sheets: [] })),
  },
}).createMachine({
  id: 'applicationTriggerFlow',
  initial: 'idle',
  context: ({ input }) => ({
    isAuthenticated: input.isAuthenticated,
    isAuthInitialized: input.isAuthInitialized,
    spots: [],
    sheets: [],
    gps: { lat: 0, lon: 0, available: false },
    nearbySpots: [],
    selectedSpotId: null,
    errorMessage: null,
  }),
  states: {
    idle: {
      on: { TRIGGER: 'preparing' },
    },
    preparing: {
      type: 'parallel',
      states: {
        gpsLocating: {
          initial: 'fetching',
          states: {
            fetching: {
              invoke: {
                src: 'fetchGPS',
                onDone: {
                  target: 'done',
                  actions: assign({ gps: ({ event }) => event.output }),
                },
                onError: {
                  target: 'done',
                  actions: assign({ gps: () => ({ lat: 0, lon: 0, available: false }) }),
                },
              },
            },
            done: { type: 'final' },
          },
        },
        spotsFetching: {
          initial: 'fetching',
          states: {
            fetching: {
              invoke: {
                src: 'fetchSpots',
                onDone: {
                  target: 'done',
                  actions: assign({
                    spots: ({ event }) => event.output.spots,
                    sheets: ({ event }) => event.output.sheets,
                  }),
                },
                onError: { target: '#applicationTriggerFlow.error' },
              },
            },
            done: { type: 'final' },
          },
        },
      },
      onDone: 'sortingSpots',
    },
    sortingSpots: {
      always: [
        {
          target: 'selectingSpot',
          actions: assign({
            nearbySpots: ({ context }) => buildNearbySpots(context),
          }),
        },
      ],
    },
    selectingSpot: {
      on: {
        SPOT_SELECTED: {
          target: 'spotSelected',
          actions: assign({ selectedSpotId: ({ event }) => event.spotId }),
        },
        DIALOG_CLOSE: 'idle',
      },
    },
    spotSelected: {
      always: 'idle',
    },
    error: {
      on: { RESET: 'idle', TRIGGER: 'idle' },
    },
  },
})

function buildNearbySpots(context: ApplicationTriggerContext): NearbySpotItem[] {
  const MAX_DISPLAY = 3

  // スポット一覧（/spots）と同じ基準でロック状態を解決する
  const sheetById = buildSheetById(context.sheets)

  // 現在押印可能なスタンプを持つスポットを優先表示する
  const stampableSpots = context.spots.filter((s) => s.hasStamp)
  // 所属シートが全てロック中のスポットは押印できない（hasStamp=false）ため上記に含まれない。
  // 一覧と同様に灰色表示で存在だけは知らせるため、残り枠を埋める候補として後ろに並べる。
  const lockedSpots = context.spots.filter((s) => !s.hasStamp && isSpotFullyLocked(s, sheetById))

  const toSortedCandidates = (spots: SpotData[]) => {
    const candidates = spots.map((s) => {
      const hasCoords = s.latitude != null && s.longitude != null
      const distanceKm =
        context.gps.available && hasCoords
          ? haversineDistance(
              { latitude: context.gps.lat, longitude: context.gps.lon },
              { latitude: s.latitude as number, longitude: s.longitude as number },
              'km',
            )
          : null
      return { spot: s, distanceKm }
    })
    return context.gps.available
      ? candidates.sort((a, b) => (a.distanceKm ?? 0) - (b.distanceKm ?? 0))
      : candidates.sort((a, b) => a.spot.id - b.spot.id)
  }

  const sorted = [...toSortedCandidates(stampableSpots), ...toSortedCandidates(lockedSpots)]

  return sorted.slice(0, MAX_DISPLAY).map(({ spot, distanceKm }) => ({
    spotId: spot.id,
    spotName: spot.name,
    address: spot.address ?? null,
    distanceKm,
    isAcquired: false,
    imageUrl: spot.images[0] ?? null,
    grayedOut: isSpotFullyLocked(spot, sheetById),
  }))
}

export function useApplicationTriggerFlow() {
  const queryClient = useQueryClient()
  const spotsFetchFn = useTanstackSpotsFetchFn()
  const idToken = useTokenStore((s) => s.idToken)
  const tokenLoading = useTokenStore((s) => s.loading)

  const [state, send] = useMachine(
    applicationTriggerMachineBase.provide({
      actors: {
        fetchGPS: fromPromise(async () => getGPS()),
        fetchSpots: fromPromise(async () => {
          const res = await queryClient.fetchQuery(spotsFetchFn)
          return { spots: res?.spots ?? [], sheets: res?.sheets ?? [] }
        }),
      },
    }),
    {
      input: (() => {
        const tokenState = useTokenStore.getState()
        return {
          isAuthenticated: !tokenState.loading && tokenState.idToken !== null,
          isAuthInitialized: !tokenState.loading,
        }
      })(),
    },
  )

  useEffect(() => {
    if (tokenLoading) return
    send({ type: 'AUTH_READY', isAuthenticated: idToken !== null })
  }, [tokenLoading, idToken, send])

  return {
    isSelectingSpot: state.matches('selectingSpot'),
    /** GPS 測位 + スポット一覧取得の待機中（GET タップ直後〜ダイアログ表示まで）。
     *  preparing は parallel state だが、どちらの子ステートに居ても matches は true を返す */
    isPreparing: state.matches('preparing'),
    gpsAvailable: state.context.gps.available,
    nearbySpots: state.context.nearbySpots,
    trigger: () => send({ type: 'TRIGGER' }),
    selectSpot: (spotId: number) => send({ type: 'SPOT_SELECTED', spotId }),
    closeDialog: () => send({ type: 'DIALOG_CLOSE' }),
  }
}
